import { DEMO_USER } from "../demoMsalAccount.ts";
import {
  DEMO_COMPANIES,
  DEMO_COMPANY_BY_ID,
  type DemoCompany,
} from "./companies.ts";
import { ALL_FEATURE_ENTITLEMENTS } from "./featureCatalog.ts";
import {
  DEMO_CLIENT_ROLE_BY_COMPANY,
  DEMO_LIVE_PERSON_ID,
  DEMO_REGISTERED_ROLE_ID,
  DEMO_USER_ID,
  DEMO_USER_USERNAME,
} from "./identifiers.ts";

const NOW_ISO = "2026-05-21T08:00:00.000Z";

// Anna's role within each company mapped to the real PartyRoleRelationType enum.
const ANNA_ROLE_TO_RELATION: Record<
  DemoCompany["annaRole"],
  string | undefined
> = {
  CFO: "FinancialSpecialist",
  Admin: "WorkspaceOwner",
  Employee: "Member",
};

const ALL_AVAILABLE_ACTIONS = [
  "HaveAccessInternalPortalClients",
  "HaveAccessInternalPortalRequests",
  "HaveAccessInternalPortalOnboarding",
  "HaveAccessInternalPortalLiquidityManagement",
  "HaveAccessMobileKeyActivation",
  "HaveAccessHelpCenter",
  "HaveAccessInternalPortalTransferRequests",
  "HaveAccessInternalPortalLiquidityWithdrawal",
  "HaveAccessInternalPortalMasterAccounts",
];

const registeredUserRole = {
  roleBasicInfo: {
    roleType: "RegisteredUser",
    roleName: `${DEMO_USER.firstName} ${DEMO_USER.lastName}`,
    jobTitle: "CFO",
    roleTypeLastChange: NOW_ISO,
  },
  identifiers: [
    {
      partyIdentifierType: "UserName",
      partyIdentifierValue: DEMO_USER_USERNAME,
    },
    {
      partyIdentifierType: "MfaPhone",
      partyIdentifierValue: DEMO_USER.phoneNumber,
    },
    // The userPreferences query (bw-2606) resolves the preferences documentId
    // from the RegisteredUser role's UserId identifier — without it the
    // UserContextLoader errors out before rendering anything.
    {
      partyIdentifierType: "UserId",
      partyIdentifierValue: DEMO_USER_ID,
    },
  ],
  partyRoleReference: {
    partyId: DEMO_USER_ID,
    partyRoleId: DEMO_REGISTERED_ROLE_ID,
    partyRoleType: "RegisteredUser",
  },
  availableActions: ALL_AVAILABLE_ACTIONS,
  features: ALL_FEATURE_ENTITLEMENTS,
  contracts: [],
};

// One ClientUser role per holding entity. `masterRoleRef.documentType ===
// "LiveCompany"` is the filter `useCompanyList()` uses for the sidebar.
const clientRoles = DEMO_COMPANIES.map((company) => ({
  roleBasicInfo: {
    roleType: "ClientUser",
    roleName: `${DEMO_USER.firstName} ${DEMO_USER.lastName} @ ${company.shortName}`,
    jobTitle: company.annaRole === "CFO" ? "CFO" : "Other",
    jobTitleCustom: company.annaRole,
    roleTypeLastChange: NOW_ISO,
    masterRoleRef: {
      documentId: company.id,
      partyRoleId: `${company.id}-master`,
      entityId: `${company.id}-entity`,
      documentType: "LiveCompany",
    },
    masterRoleDisplayInfo: {
      roleType: "ClientCompany",
      roleName: company.name,
      registrationCountry: company.jurisdiction,
    },
    masterRoleExternalRefs: {
      companyRegistrationNumber: company.registrationNumber,
      vatNumber: company.vatNumber,
    },
  },
  roleRelationType: ANNA_ROLE_TO_RELATION[company.annaRole],
  identifiers: [
    {
      partyIdentifierType: "UserName",
      partyIdentifierValue: DEMO_USER_USERNAME,
    },
  ],
  partyRoleReference: {
    partyId: DEMO_USER_ID,
    partyRoleId: DEMO_CLIENT_ROLE_BY_COMPANY[company.id],
    partyRoleType: "ClientUser",
  },
  availableActions: ALL_AVAILABLE_ACTIONS,
  features: ALL_FEATURE_ENTITLEMENTS,
  contracts: [
    { productType: "PrimaryAccount", contractStatus: "Active" },
    { productType: "Marketplace", contractStatus: "Active" },
    { productType: "CashManagement", contractStatus: "Active" },
    // Accounts payable is gated by ProductGuard on an Active "AP" contract —
    // without it the payables tab shows only the locked marketing page.
    { productType: "AP", contractStatus: "Active" },
  ],
}));

export const demoUserContextResponse = {
  roles: [registeredUserRole, ...clientRoles],
  invitations: [],
  internalInvitations: [],
  livePerson: {
    documentHeader: {
      id: DEMO_LIVE_PERSON_ID,
      definitionVersion: "1.0.0",
      _ts: 1747843200,
      _etag: '"demo-etag"',
      documentType: "LivePerson",
    },
    personBasicInfo: {
      firstName: DEMO_USER.firstName,
      surname: DEMO_USER.lastName,
    },
  },
};

// Onboarding variant — a freshly-registered user with NO company roles and no
// invitations. Served by getUserContext while the demo "onboarding mode" flag
// is set (see demoOnboarding.ts), so the role guard routes into the join flow
// (/join/company-lookup) instead of the dashboard. Keeps the RegisteredUser
// role (with its partyRoleReference) so the onboarding pages still authorize.
export const demoOnboardingUserContextResponse = {
  ...demoUserContextResponse,
  roles: [registeredUserRole],
  invitations: [],
  internalInvitations: [],
};

// Convenience accessor (used by mockHandlers when serving company-specific data).
export { DEMO_COMPANY_BY_ID };
